import { APIDocumentArchiveFolder, ResourceType } from '@ardoq/api-types';
import {
  ObservableState,
  persistentReducedStream,
  reducer,
  streamReducer,
  toPersistentStream,
} from '@ardoq/rxbeach';
import { DocumentArchiveContext } from '@ardoq/api-types';
import { map } from 'rxjs';
import { filterResourceType, websocket$ } from 'sync/websocket$';
import { websocketReducer } from '../crud/reducers';
import { removeEntityById, updateEntity } from '@ardoq/common-helpers';
import {
  getStreamCollectionInitialState,
  toStreamCollection,
} from '../utils/streamUtils';
import {
  setOrgDocumentArchiveFolders,
  setWorkspaceDocumentArchiveFolders,
} from './actions';
import { fixDataOperations, toFolderContext } from './fixDataOperations';
import { FolderWithContext, DocumentArchiveFoldersState } from './types';

export const isWorkspaceFolder = (folder: APIDocumentArchiveFolder): boolean =>
  toFolderContext(folder) === DocumentArchiveContext.WS;

// Replaces all folders
const handleSetWorkspaceFolders = (
  state: APIDocumentArchiveFolder[],
  folders: APIDocumentArchiveFolder[]
): APIDocumentArchiveFolder[] => {
  const orgFolders = state.filter(folder => !isWorkspaceFolder(folder));
  return [...orgFolders, ...folders];
};

// Sets org folders while keeping workspace folders intact
const handleSetOrgFolders = (
  state: APIDocumentArchiveFolder[],
  orgFolders: APIDocumentArchiveFolder[]
): APIDocumentArchiveFolder[] => {
  // Keep existing workspace folders
  const workspaceFolders = state.filter(folder => isWorkspaceFolder(folder));

  return [...workspaceFolders, ...orgFolders];
};

const handleFolderCreated = (
  state: APIDocumentArchiveFolder[],
  folder: APIDocumentArchiveFolder
): APIDocumentArchiveFolder[] => [...state, folder];

const _documentArchiveFolders$ = persistentReducedStream<
  APIDocumentArchiveFolder[]
>(
  '_documentArchiveFolders$',
  [],
  [
    reducer(setOrgDocumentArchiveFolders, handleSetOrgFolders),
    reducer(setWorkspaceDocumentArchiveFolders, handleSetWorkspaceFolders),
    streamReducer(
      websocket$.pipe(
        filterResourceType<APIDocumentArchiveFolder>(
          ResourceType.DOCUMENT_ARCHIVE_FOLDER
        )
      ),
      websocketReducer({
        create: handleFolderCreated,
        update: updateEntity,
        delete: removeEntityById,
      })
    ),
  ]
);

const addContextToFolders = (
  folders: APIDocumentArchiveFolder[]
): FolderWithContext[] =>
  folders.map(folder =>
    fixDataOperations.removeSelfReferentialParent(
      fixDataOperations.addContextToFolder(folder)
    )
  );

export const documentArchiveFolders$: ObservableState<DocumentArchiveFoldersState> =
  toPersistentStream(
    'documentArchiveFolders$',
    _documentArchiveFolders$.pipe(
      map(addContextToFolders),
      map(toStreamCollection)
    ),
    getStreamCollectionInitialState()
  );
